// src/lib/sizing.js
//
// Indicative cold room heat load estimate.
//
// EVERY COEFFICIENT IN THIS FILE NEEDS BROOKE'S SIGN-OFF BEFORE LAUNCH.
// The method is the standard four-component load calculation, but the values
// below are industry typicals rather than Brooke's own design figures. An
// under-specified plant is a liability, so the model rounds up throughout and
// the interface must always present the result as indicative and subject to a
// site survey.
//
// The four components:
//
//   Transmission   heat through the envelope     U x A x dT
//   Infiltration   air exchanged at the door     volume x air changes x enthalpy
//   Product        pulling the load down          mass x specific heat x dT
//   Internal       fans, lights, people
//
// Those are summed, a safety margin is added, and the total is divided across
// the plant's running hours rather than 24, because a compressor does not run
// continuously.

// Panel U-values, W/m2K. PIR core, typical published figures.
export const PANELS = [
  { mm: 50, u: 0.44 },
  { mm: 75, u: 0.3 },
  { mm: 100, u: 0.22 },
  { mm: 150, u: 0.15 },
  { mm: 200, u: 0.11 },
];

export const ROOM_TYPES = {
  chiller: {
    label: "Chiller",
    blurb: "Fresh produce, dairy, packed goods",
    roomTemp: 2,
    minPanel: 75,
    runHours: 16,
    // kWh per m3 of air exchanged, from the enthalpy difference between
    // Gauteng design ambient and the room
    enthalpy: 0.055,
    airChangeBase: 70,
    peopleWatts: 300,
    pulldownHours: 24,
    // kg entering the room per m3 of volume per day, when the visitor does
    // not give a figure. A chiller turns over faster than a holding freezer.
    throughputPerM3: 12,
  },
  freezer: {
    label: "Freezer",
    blurb: "Frozen storage, holding",
    roomTemp: -18,
    minPanel: 100,
    runHours: 18,
    enthalpy: 0.095,
    airChangeBase: 60,
    peopleWatts: 400,
    pulldownHours: 24,
    throughputPerM3: 6,
  },
  blast: {
    label: "Blast freezer",
    blurb: "Rapid pulldown of warm product",
    roomTemp: -30,
    minPanel: 150,
    runHours: 20,
    enthalpy: 0.12,
    airChangeBase: 60,
    peopleWatts: 400,
    // The point of a blast freezer is speed, so the product load is spread
    // over a shift rather than a day. This is what makes its plant so much
    // larger than a holding freezer of the same size.
    pulldownHours: 10,
    // A blast freezer exists to move product, so it is loaded hard.
    throughputPerM3: 25,
  },
};

export const DOOR_USAGE = {
  light: { label: "Light", blurb: "A few openings an hour", factor: 0.8 },
  moderate: { label: "Moderate", blurb: "Regular traffic", factor: 1.0 },
  heavy: { label: "Heavy", blurb: "Constant loading", factor: 1.5 },
};

// Gauteng summer design ambient. Sizing on the average rather than the design
// day is the usual way a room ends up short in January.
export const DEFAULT_AMBIENT = 35;

// Product thermal properties, generic mixed foodstuff.
const CP_ABOVE = 3.6; // kJ/kgK above freezing
const CP_BELOW = 1.9; // kJ/kgK below freezing
const LATENT = 250; // kJ/kg to freeze
const FREEZE_POINT = -1;

const LIGHT_W_PER_M2 = 6;
const LIGHT_HOURS = 8;
const PEOPLE_HOURS = 2;
const FAN_FRACTION = 0.08;
const SAFETY = 0.15;

function pickPanel(minPanel, deltaT) {
  // Step up a size once the temperature difference gets severe, which is what
  // keeps the running cost sane over the life of the room.
  let target = minPanel;
  if (deltaT >= 50) target = Math.max(target, 150);
  else if (deltaT >= 40) target = Math.max(target, 100);
  return PANELS.find((p) => p.mm >= target) || PANELS[PANELS.length - 1];
}

export function calculate({
  type = "chiller",
  length = 3,
  width = 3,
  height = 2.5,
  ambient = DEFAULT_AMBIENT,
  doorUsage = "moderate",
  throughputKg = null,
  entryTemp = 25,
}) {
  const spec = ROOM_TYPES[type] || ROOM_TYPES.chiller;
  const door = DOOR_USAGE[doorUsage] || DOOR_USAGE.moderate;

  const L = Math.max(1, Number(length) || 0);
  const W = Math.max(1, Number(width) || 0);
  const H = Math.max(1.8, Number(height) || 0);

  const volume = L * W * H;
  const floorArea = L * W;
  // Six faces: floor, ceiling and four walls.
  const envelope = 2 * (L * W) + 2 * (L * H) + 2 * (W * H);

  const deltaT = ambient - spec.roomTemp;
  const panel = pickPanel(spec.minPanel, deltaT);

  // --- transmission ------------------------------------------------------
  const transmission = panel.u * envelope * deltaT;

  // --- infiltration ------------------------------------------------------
  // Air changes per day fall off with volume - a big room exchanges
  // proportionally less through the same door.
  const airChanges = spec.airChangeBase / Math.sqrt(volume);
  const infiltration =
    (volume * airChanges * spec.enthalpy * 1000 * door.factor) / 24;

  // --- product -----------------------------------------------------------
  const mass =
    throughputKg === null || throughputKg === ""
      ? volume * spec.throughputPerM3
      : Math.max(0, Number(throughputKg) || 0);

  let productKJ = 0;
  if (spec.roomTemp > FREEZE_POINT) {
    // Chiller: sensible cooling only.
    productKJ = mass * CP_ABOVE * Math.max(0, entryTemp - spec.roomTemp);
  } else {
    // Freezing: down to the freeze point, through it, then below it.
    const above = mass * CP_ABOVE * Math.max(0, entryTemp - FREEZE_POINT);
    const latent = mass * LATENT;
    const below = mass * CP_BELOW * Math.max(0, FREEZE_POINT - spec.roomTemp);
    productKJ = above + latent + below;
  }
  const product = (productKJ * 1000) / (spec.pulldownHours * 3600);

  // --- internal ----------------------------------------------------------
  const lights = (LIGHT_W_PER_M2 * floorArea * LIGHT_HOURS) / 24;
  const people = (spec.peopleWatts * PEOPLE_HOURS) / 24;

  const base = transmission + infiltration + product + lights + people;
  const fans = base * FAN_FRACTION;

  const subtotal = base + fans;
  const total = subtotal * (1 + SAFETY);

  // Plant has to do the day's work in its running hours, not in 24.
  const plantW = (total * 24) / spec.runHours;

  const kw = (w) => Math.round((w / 1000) * 100) / 100;

  return {
    inputs: { type, L, W, H, ambient, doorUsage, mass, entryTemp },
    spec,
    door,
    panel,
    volume: Math.round(volume * 10) / 10,
    floorArea: Math.round(floorArea * 10) / 10,
    envelope: Math.round(envelope * 10) / 10,
    deltaT,
    loads: {
      transmission: kw(transmission),
      infiltration: kw(infiltration),
      product: kw(product),
      internal: kw(lights + people + fans),
    },
    totalKw: kw(total),
    // Rounded up to the nearest 0.5 kW, because rounding down is the failure
    // that matters.
    plantKw: Math.ceil((plantW / 1000) * 2) / 2,
  };
}

// A plain-text summary, sent through with the enquiry so the office receives a
// brief rather than a request for a call.
export function toBrief(r) {
  return [
    `Indicative sizing — ${r.spec.label}`,
    `Internal size: ${r.inputs.L} x ${r.inputs.W} x ${r.inputs.H} m (${r.volume} m3)`,
    `Room temperature: ${r.spec.roomTemp} C, ambient ${r.inputs.ambient} C`,
    `Door usage: ${r.door.label}`,
    `Daily throughput: ${Math.round(r.inputs.mass)} kg at ${r.inputs.entryTemp} C`,
    `Recommended panel: ${r.panel.mm} mm`,
    `Estimated load: ${r.totalKw} kW`,
    `Indicative plant capacity: ${r.plantKw} kW`,
    "",
    "Generated by the sizing estimator on the website. Indicative only, subject to site survey.",
  ].join("\n");
}